// src/components/ClientList.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import ClientService from "../services/ClientService";
import { AuthContext } from "../context/AuthContext";
import { MdDelete } from "react-icons/md";
import { FaEye } from "react-icons/fa";

const ClientList = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const { user, getClients, clients, clientLoading, clientError, } = useContext(AuthContext);


  useEffect(() => {
    if (!clients.length) {
      getClients();
    }
  }, [getClients, clients]);


  useEffect(() => {
    // Get user info from localStorage
    setUserInfo(user);

    
    

    // Verify admin access
    if (!["ADMIN", "CEO", "VAIBHAV", "EMP"].includes(user?.role)) {
      setError(
        "Access denied. You do not have permission to view all clients."
      );
      setLoading(false);
      return;
    }

    
  }, []);

  const handleDeleteClient = async (clientId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this client? This action cannot be undone."
      )
    ) {
      try {
        setLoading(true);
        await ClientService.deleteClient(clientId);
        // setClients(clients.filter((client) => client._id !== clientId));
        setLoading(false);
      } catch (err) {
        console.error("Error deleting client:", err);
        setError("Failed to delete client. Please try again.");
        setLoading(false);
      }
    }
  };

  // Filter clients based on search term
  const filteredClients = clients.filter((client) => {
    const searchableText = [
      client.companyName,
      client.user?.name,
      client.user?.email,
      client.address,
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(searchTerm.toLowerCase());
  });

  if (clientLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (clientError) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500">Error</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md my-4" role="alert">
        <p className="font-bold">Error</p>
        <p>{error}</p>
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-4 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">All Clients</h1>
        <div className="flex items-center">
          <div className="relative mr-4">
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
          <button
            onClick={() => navigate("/dashboard/client/add")}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md"
          >
            Add New Client
          </button>
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div className="bg-yellow-100 text-yellow-700 p-4 rounded-md my-4">
          <p className="font-medium">No clients found</p>
          {searchTerm ? (
            <p>
              No results match your search criteria. Try a different search
              term.
            </p>
          ) : (
            <p>There are no clients in the system yet.</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Company Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Contact
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Projects
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.map((client) => (
                <tr 
                key={client._id} 
                className="bg-gray-50 hover:bg-gray-200"
                onClick={()=> navigate(`/dashboard/client/${client.user?._id}`)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {client.companyName || "Not specified"}
                    </div>
                    <div className="text-sm text-gray-500">
                      {client.address
                        ? client.address.length > 30
                          ? `${client.address.substring(0, 30)}...`
                          : client.address
                        : "No address"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {client.user ? (
                      <>
                        <div className="text-sm font-medium text-gray-900">
                          {client.user.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {client.user.email || "No email"}
                        </div>
                        {client.user.phone && (
                          <div className="text-sm text-gray-500">
                            {client.user.phone}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-500">No user data</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {client.user?.status ? (
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          client.user.status === "active"
                            ? "bg-green-100 text-green-800"
                            : client.user.status === "inactive"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {client.user.status.toUpperCase()}
                      </span>
                    ) : (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        Unknown
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {client.project ? client.project.length : 0} projects
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() =>
                        navigate(`/dashboard/client/${client.user?._id}`)
                      }
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <FaEye className="text-lg" />
                    </button>
                    {/* <button
                      onClick={() => navigate(`/dashboard/client/edit/${client._id}`)}
                      className="text-green-600 hover:text-green-900 mr-3"
                    >
                      Edit
                    </button> */}
                    {userInfo?.role === "ADMIN" && (
                      <button
                        onClick={() => handleDeleteClient(client._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <MdDelete className="text-lg" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* <div className="mt-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-blue-500 hover:text-blue-700 font-medium"
        >
          Back to Dashboard
        </button>
      </div> */}
    </div>
  );
};

export default ClientList;
